/**
 * Elimination Bracket Utilities
 * 
 * Implements TOD Section 4.2-4.3:
 * - Perfect power-of-2 brackets (8, 16, 32, 64)
 * - Standard seeding (1v16, 2v15, etc.)
 * - 3-point scale for elimination bouts
 * - Configurable rounds per stage
 */

/**
 * Get available bracket sizes for a given number of fighters.
 * Returns power-of-2 options with percentage advancing.
 */
export function getBracketOptions(totalFighters) {
  const powers = [4, 8, 16, 32, 64];
  return powers
    .filter(size => size <= totalFighters)
    .map(size => ({
      size,
      percentage: Math.round((size / totalFighters) * 100),
      recommended: (size / totalFighters) >= 0.35 && (size / totalFighters) <= 0.60,
    }));
}

/**
 * Generate standard bracket seeding.
 * 1 vs N, 2 vs N-1, etc. — ensures top seeds don't meet until later rounds.
 * 
 * For a bracket of size N, generates N/2 first-round matchups.
 * Uses proper tournament seeding so that if favorites win,
 * seed 1 meets seed 2 in the final.
 */
export function generateBracketSeeding(rankedFighters, advancingCount) {
  // Round up to next power of 2 for the actual bracket
  const bracketSize = Math.pow(2, Math.ceil(Math.log2(Math.max(2, advancingCount))));
  const fighters = rankedFighters.slice(0, advancingCount);
  
  // Pad with BYEs to fill the bracket
  while (fighters.length < bracketSize) {
    fighters.push({ name: "BYE", rank: fighters.length + 1, isBye: true });
  }

  // Generate proper bracket positions using standard tournament seeding
  const positions = generateSeedPositions(bracketSize);
  
  const matchups = [];
  for (let i = 0; i < positions.length; i += 2) {
    const seed1 = positions[i];
    const seed2 = positions[i + 1];
    matchups.push({
      matchIndex: matchups.length,
      fighter1: fighters[seed1 - 1] || { name: "BYE", isBye: true },
      fighter2: fighters[seed2 - 1] || { name: "BYE", isBye: true },
      seed1,
      seed2,
    });
  }

  return matchups;
}

/**
 * Generate seed positions for a bracket.
 * This ensures 1 vs N, 2 vs N-1 pattern with proper bracket placement
 * so higher seeds meet later in the tournament.
 */
function generateSeedPositions(size) {
  if (size === 2) return [1, 2];
  
  const prev = generateSeedPositions(size / 2);
  const result = [];
  
  for (const seed of prev) {
    result.push(seed);
    result.push(size + 1 - seed);
  }
  
  return result;
}

/**
 * Calculate the number of rounds in a bracket.
 */
export function getBracketRounds(bracketSize) {
  return Math.log2(bracketSize);
}

/**
 * Get the stage name for a given round in the bracket.
 */
export function getStageName(bracketSize, roundIndex) {
  const remaining = bracketSize / Math.pow(2, roundIndex);
  if (remaining === 2) return "Final";
  if (remaining === 4) return "Semi-Finals";
  if (remaining === 8) return "Quarter-Finals";
  return `Round of ${remaining}`;
}

/**
 * Get default rounds-per-bout for each elimination stage.
 * Per TOD rules: R32 can be 1 round, R16+ should be 3 rounds.
 */
export function getDefaultStageConfig(bracketSize) {
  const totalRounds = getBracketRounds(bracketSize);
  const config = [];
  
  for (let i = 0; i < totalRounds; i++) {
    const remaining = bracketSize / Math.pow(2, i);
    const stageName = getStageName(bracketSize, i);
    config.push({
      roundIndex: i,
      stageName,
      remainingFighters: remaining,
      boutsInStage: remaining / 2,
      roundsPerBout: remaining > 16 ? 1 : 3,
      phase: "elimination", // default 3-pt, admin can change to "pool" for 5-pt
    });
  }
  
  return config;
}

/**
 * Build the full bracket structure from matchups and results.
 * Returns an array of stages, each with their matchups and results.
 */
export function buildBracketView(bracketSize, firstRoundMatchups, bouts) {
  const totalRounds = getBracketRounds(bracketSize);
  const stages = [];
  
  for (let round = 0; round < totalRounds; round++) {
    const stageName = getStageName(bracketSize, round);
    const matchCount = bracketSize / Math.pow(2, round + 1);
    
    if (round === 0) {
      // First round uses the seeded matchups
      stages.push({
        round,
        stageName,
        matchups: firstRoundMatchups.map((m, idx) => {
          const bout = bouts.find(b => 
            b.eliminationRound === 0 && b.eliminationMatch === idx
          );
          return {
            ...m,
            bout,
            winner: bout?.confirmedResult?.winner || null,
          };
        }),
      });
    } else {
      // Later rounds — matchups determined by winners of previous round
      const prevStage = stages[round - 1];
      const matchups = [];
      
      for (let i = 0; i < matchCount; i++) {
        const prev1 = prevStage.matchups[i * 2];
        const prev2 = prevStage.matchups[i * 2 + 1];
        
        const fighter1Name = prev1?.winner || (prev1?.fighter1?.isBye ? prev1.fighter2.name : prev1?.fighter2?.isBye ? prev1.fighter1.name : "TBD");
        const fighter2Name = prev2?.winner || (prev2?.fighter1?.isBye ? prev2.fighter2.name : prev2?.fighter2?.isBye ? prev2.fighter1.name : "TBD");
        
        const bout = bouts.find(b => 
          b.eliminationRound === round && b.eliminationMatch === i
        );
        
        matchups.push({
          matchIndex: i,
          fighter1: { name: fighter1Name, isBye: fighter1Name === "BYE" },
          fighter2: { name: fighter2Name, isBye: fighter2Name === "BYE" },
          bout,
          winner: bout?.confirmedResult?.winner || null,
        });
      }
      
      stages.push({ round, stageName, matchups });
    }
  }
  
  return stages;
}
